import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FEATURED_JOBS } from "@/mocks/jobs";

type SortKey = "urgency" | "pay" | "distance" | "match";

const CATEGORIES = ["All", "Landscaping", "Moving / Labor", "Cleaning", "Construction", "Events", "Personal Services", "Driving / Delivery"];

const URGENCY_ORDER: Record<string, number> = { URGENT: 0, TODAY: 1, TOMORROW: 2, "THIS WEEK": 3, FLEXIBLE: 4 };
const URGENCY_COLORS: Record<string, string> = {
  URGENT: "bg-red-100 text-red-700",
  TODAY: "bg-red-50 text-red-600",
  TOMORROW: "bg-orange-100 text-orange-700",
  "THIS WEEK": "bg-yellow-100 text-yellow-700",
  FLEXIBLE: "bg-green-100 text-green-700",
};

function JobCard({ job, applied, onApply, onView }: { job: (typeof FEATURED_JOBS)[0]; applied: boolean; onApply: (id: number) => void; onView: (id: number) => void }) {
  return (
    <div
      onClick={() => onView(job.id)}
      className={`bg-white rounded-2xl border p-5 flex flex-col gap-4 hover:border-emerald-200 transition-all duration-200 cursor-pointer group ${applied ? "opacity-55 border-gray-100" : "border-gray-200"}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-50 border border-gray-100 shrink-0">
            <i className={`${job.icon} text-xl text-gray-600`} />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-sm leading-snug">{job.title}</h4>
            <p className="text-xs text-gray-500">{job.company}</p>
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${URGENCY_COLORS[job.urgency] ?? "bg-gray-100 text-gray-600"}`}>
          {job.urgency}
        </span>
      </div>

      <div className="flex items-center gap-4">
        <div>
          <div className="text-xl font-extrabold text-gray-900">{job.pay}</div>
          <div className="text-xs text-gray-400">pay rate</div>
        </div>
        <div className="w-px h-8 bg-gray-100" />
        <div>
          <div className="text-sm font-bold text-gray-700">{job.distance}</div>
          <div className="text-xs text-gray-400">distance</div>
        </div>
        <div className="w-px h-8 bg-gray-100" />
        <div>
          <div className="text-sm font-bold text-gray-700">{job.slots}</div>
          <div className="text-xs text-gray-400">open slots</div>
        </div>
      </div>

      <p className="text-xs text-gray-500 leading-relaxed">{job.description}</p>

      <div className="flex flex-wrap gap-1.5">
        {job.capabilities.map((cap) => (
          <span key={cap} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-lg">
            {cap}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 flex items-center justify-center bg-green-50 rounded-full">
            <i className="ri-bar-chart-fill text-green-600 text-xs" />
          </div>
          <span className="text-xs font-semibold text-green-700">{job.match}% match</span>
          <span className="text-xs text-gray-400 ml-2">{job.posted}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onApply(job.id); }}
          disabled={applied}
          className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
            applied ? "bg-green-100 text-green-700" : "bg-emerald-500 text-white hover:bg-emerald-600"
          }`}
        >
          {applied ? <><i className="ri-check-line mr-1" />Applied</> : "Quick Apply"}
        </button>
      </div>
    </div>
  );
}

export default function JobListings() {
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<SortKey>("urgency");
  const [activeCategory, setActiveCategory] = useState("All");
  const [appliedIds, setAppliedIds] = useState<number[]>([]);

  const filtered = FEATURED_JOBS.filter((j) => activeCategory === "All" || j.category === activeCategory);

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "pay") return b.payNum - a.payNum;
    if (sortBy === "distance") return a.distanceNum - b.distanceNum;
    if (sortBy === "match") return b.match - a.match;
    return (URGENCY_ORDER[a.urgency] ?? 5) - (URGENCY_ORDER[b.urgency] ?? 5);
  });

  const handleApply = (id: number) => setAppliedIds((p) => (p.includes(id) ? p : [...p, id]));
  const handleView = (id: number) => navigate(`/jobs/${id}`);

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      {/* Category Pills — scrollable on mobile, wrap on desktop */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap scrollbar-hide">
        {CATEGORIES.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(cat)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-full border transition-colors cursor-pointer whitespace-nowrap flex-shrink-0 ${
              activeCategory === cat
                ? "bg-emerald-500 text-white border-emerald-500"
                : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:text-emerald-600"
            }`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Sort + Count Bar */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-gray-600">Sort by:</span>
          {(["urgency", "pay", "match", "distance"] as SortKey[]).map((key) => (
            <button
              key={key}
              onClick={() => setSortBy(key)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors cursor-pointer whitespace-nowrap capitalize ${
                sortBy === key ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
              }`}
            >
              {key === "urgency" ? "Most Urgent" : key === "pay" ? "Highest Pay" : key === "match" ? "Best Match" : "Nearest"}
            </button>
          ))}
        </div>
        <span className="text-sm text-gray-400 sm:ml-auto">{sorted.length} opportunities found</span>
      </div>

      {/* Applied Banner */}
      {appliedIds.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 md:px-5 py-3 flex items-center gap-3">
          <i className="ri-check-double-line text-emerald-600 text-lg shrink-0" />
          <span className="text-sm font-semibold text-emerald-800">
            Applied to {appliedIds.length} job{appliedIds.length > 1 ? "s" : ""}! AI is generating your tailored application materials.
          </span>
        </div>
      )}

      {/* Job Grid */}
      {sorted.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {sorted.map((job) => (
            <JobCard key={job.id} job={job} applied={appliedIds.includes(job.id)} onApply={handleApply} onView={handleView} />
          ))}
        </div>
      ) : (
        <div className="py-16 md:py-20 text-center text-gray-400">
          <i className="ri-search-line text-4xl mb-3 block" />
          <p className="font-semibold">No jobs in this category right now</p>
          <p className="text-sm mt-1">Try selecting "All" or a different filter</p>
        </div>
      )}

      <div className="text-center mt-1 md:mt-2">
        <button className="px-8 py-3 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:border-emerald-400 hover:text-emerald-600 transition-colors cursor-pointer whitespace-nowrap">
          Load More Jobs <i className="ri-arrow-down-line ml-1" />
        </button>
      </div>
    </div>
  );
}
